/**
 * Simple logger utility for debugging
 * Can be extended to support different log levels, file output, etc.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: any;
  timestamp: string;
}

class Logger {
  private logs: LogEntry[] = [];
  private maxLogs = 1000; // Keep last 1000 logs in memory
  private enabled = true;

  /**
   * Enable or disable logging
   */
  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }

  /**
   * Add a log entry
   */
  private addLog(level: LogLevel, message: string, data?: any) {
    if (!this.enabled) return;

    const entry: LogEntry = {
      level,
      message,
      data,
      timestamp: new Date().toISOString(),
    };

    this.logs.push(entry);

    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Also output to console with appropriate method
    const consoleMethod = level === 'error' ? console.error : 
                         level === 'warn' ? console.warn : 
                         level === 'info' ? console.info : 
                         console.log;
    
    const prefix = `[${level.toUpperCase()}] [${entry.timestamp}]`;
    if (data !== undefined) {
      consoleMethod(prefix, message, data);
    } else {
      consoleMethod(prefix, message);
    }
  }

  /**
   * Log debug message
   */
  debug(message: string, data?: any) {
    this.addLog('debug', message, data);
  }

  /**
   * Log info message
   */
  info(message: string, data?: any) {
    this.addLog('info', message, data);
  }

  /**
   * Log warning message
   */
  warn(message: string, data?: any) {
    this.addLog('warn', message, data);
  }

  /**
   * Log error message
   */
  error(message: string, data?: any) {
    this.addLog('error', message, data);
  }

  /**
   * Get all logs
   */
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  /**
   * Get logs filtered by level
   */
  getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.logs.filter(log => log.level === level);
  }

  /**
   * Clear all logs
   */
  clear() {
    this.logs = [];
  }

  /**
   * Export logs as JSON string
   */
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }
}

// Export singleton instance
export const logger = new Logger();

// Also export the class for testing or custom instances
export { Logger };

